// Operator left rail — NovemAI İç Panel shell dili (panel/Shell.jsx
// InternalSidebar portu, B1).
//
// Graphite chrome: the customer console rides the warm oat canvas; the
// İç Panel deliberately flips the rail to the DARK inverse surface so an
// operator always feels "behind the curtain" (mockup: "different surface
// temperature"). Token-only styling — the chrome maps to --bg-inverse and the
// --rail-* mixes in globals.css, which flip WITH the surface. Fixed ramp steps
// here measured 1.2–2.2:1 on the dark-mode (cream) rail.
//
// TWO accent families here, deliberately: the STAFF banner and the nav's
// active treatment stay on the shared emerald family via --accent-on-inverse (they say
// "you are signed in / you are here", the same vocabulary as the customer
// console), while the WORDMARK badge moved to --brand-operator (spec 44
// color_roles.brand_operator). Only the mark carries product identity, and
// only the mark is reproduced in the browser tab — recolouring the status
// vocabulary too would have changed what "active" means, not what product this is.
//
// --bg-inverse INVERTS with the viewer's theme, so this rail is a dark surface
// for a light-mode operator and a LIGHT one for a dark-mode operator. The
// badge therefore takes a MODE-VARYING fill, and the two steel-blue steps this
// badge wore before are both here — one per mode. Neither was a bad colour;
// each was measured on one surface and shipped as a single value, which meant
// each was silently failing WCAG 1.4.11 on the surface nobody looked at.
//
// The favicon does not share this constraint and never did: it paints its own
// background, so it owes contrast only to its own glyph. Binding it to the
// rail's floor is what made the palette look like it had four usable colours.
// See color_roles.brand_operator in packages/design-tokens/src/colors.ts; the
// badge×rail and glyph×badge pairs are computed and asserted PER MODE in that
// package's contrast.ts / contrast.test.ts.
//
// INTERNAL identity (spec 60 mistake-prevention): the STAFF banner at
// the very top of the rail (emerald status dot + "Personel konsolu"
// overline + serif italic byline) is the always-visible marker that this
// is the staff console, paired with the TopBar's INTERNAL badge.
//
// Nav is sectioned per the mockup (İşletme / Ticaret / Ürün); items are
// NavLinks with the mockup's active treatment (emerald left bar + icon
// tint + raised row). Routes that haven't shipped yet fall through the
// router's catch-all → home, same as the previous plain-anchor scaffold.

import {
  Activity,
  Building2,
  CreditCard,
  Download,
  Eye,
  Gauge,
  Inbox,
  KeyRound,
  Landmark,
  LifeBuoy,
  LogOut,
  ToggleRight,
  UserCog,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { useAuth } from "react-oidc-context";

import { OPERATOR_ACTIONS } from "@/lib/operator-permissions";
import { cn } from "@/lib/utils";
import { useUiStore } from "@/store/ui";
import { useOperatorMe } from "@/hooks/useOperatorMe";

/**
 * The eight-role allowlist ops-tenants / ops-health / ops-feature-flags share
 * (everything except role_op_auditor and role_op_revenue). Mirrors the frozen
 * lists in those route files — the rail must not advertise a surface the route
 * will refuse.
 */
const OPS_BROAD = [
  "role_op_staff",
  "role_op_customer_success",
  "role_op_support",
  "role_op_finance",
  "role_op_sre",
  "role_op_sales",
  "role_op_exec",
  "role_op_admin",
] as const;

const NAV_SECTIONS = [
  {
    key: "operate",
    items: [
      { key: "home", icon: Gauge, href: "/" },
      { key: "tenants", icon: Building2, href: "/tenants", anyRole: OPS_BROAD },
      {
        key: "support_inquiries",
        icon: LifeBuoy,
        href: "/support-inquiries",
        anyRole: OPERATOR_ACTIONS["support_inquiries.read"],
      },
      { key: "health", icon: Activity, href: "/health", anyRole: OPS_BROAD },
      { key: "dlq", icon: Inbox, href: "/dlq", anyRole: OPERATOR_ACTIONS["dlq.read"] },
      { key: "audit", icon: Eye, href: "/audit", anyRole: ["role_op_admin", "role_op_auditor", "role_op_exec"] },
      { key: "impersonations", icon: UserCog, href: "/impersonations", anyRole: ["role_op_admin", "role_op_customer_success", "role_op_support"] },
      { key: "exports", icon: Download, href: "/exports", anyRole: ["role_op_admin", "role_op_finance"] },
      // 'incidents' nav removed: no /incidents route exists yet, so the click
      // silently bounced to home via the router catch-all (masqueraded as
      // working). Re-add when the route ships. (SEM-322 audit)
    ],
  },
  {
    key: "commerce",
    items: [
      { key: "billing", icon: CreditCard, href: "/billing", anyRole: ["role_op_admin"] },
      // /ops/v1/onprem-licenses admits role_op_revenue EXACTLY — the route
      // comment records that role_op_admin is deliberately not an alternate
      // (spec 63 separation of duties). Ungated, the rail offered every
      // operator a link that answered "Yetki yok": the same
      // don't-offer-what-can't-be-done rule that already deleted the
      // 'incidents' entry above.
      { key: "licenses", icon: KeyRound, href: "/licenses", anyRole: ["role_op_revenue"] },
    ],
  },
  {
    key: "product",
    items: [
      { key: "flags", icon: ToggleRight, href: "/flags", anyRole: OPS_BROAD },
      // /ops/v1/payroll-parameter-sets admits role_op_payroll_ops EXACTLY
      // (spec 60 §feat_op_statutory_parameter_management); role_op_admin is
      // not an alternate, so the entry is gated the same way Licenses is.
      {
        key: "payroll_parameters",
        icon: Landmark,
        href: "/payroll-parameters",
        anyRole: ["role_op_payroll_ops"],
      },
    ],
  },
] as const;

export function LeftRail(): React.ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const expanded = useUiStore((s) => s.leftRailExpanded);
  const mobileOpen = useUiStore((s) => s.mobileNavOpen);
  const setMobileNavOpen = useUiStore((s) => s.setMobileNavOpen);
  // useOperatorMe's cache is tuned for exactly this (its docstring: "nav/
  // permission decisions"). Fail CLOSED while roles are unknown — a gated
  // entry that flashes before disappearing is worse than one that appears a
  // beat late, and an operator must never be shown a separation-of-duties
  // surface they do not hold.
  const { data: me } = useOperatorMe();
  const roles: readonly string[] = me?.roles ?? [];
  // NAV_SECTIONS is `as const`, so only the gated entry carries requiredRole;
  // read it structurally rather than widening every item's literal type.
  const maySee = (item: Readonly<Record<string, unknown>>): boolean => {
    const allowed = item["anyRole"];
    if (!Array.isArray(allowed)) return true; // ungated (Overview)
    return allowed.some((r) => typeof r === "string" && roles.includes(r));
  };

  const userLabel =
    auth.user?.profile.name ??
    auth.user?.profile.preferred_username ??
    auth.user?.profile.sub ??
    "operator";
  const initials = userLabel
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      {/* Karartma yalnız dar ekranda var; geniş ekranda çubuk akışın parçası,
          örtecek bir şey yok. Düğme, çünkü tıklanabilir bir <div> klavyeyle
          erişilemez ve panelin tek kapatma yolu bu olurdu. */}
      {mobileOpen && (
        <button
          type="button"
          aria-label={t("nav.close_menu")}
          className="nai-rail-scrim md:hidden"
          onClick={(): void => {
            setMobileNavOpen(false);
          }}
        />
      )}
      <aside
        className={cn(
          "flex flex-col border-r border-[var(--rail-line)] bg-[var(--bg-inverse)]",
          "text-[var(--fg-on-inverse)]",
          // Dar ekranda AKIŞTAN ÇIKAR: `w-60` akışta kalsaydı 375px'lik bir
          // ekranın 240'ını yer ve kalan sütun içeriğine sığmadığı için sayfa
          // yanlamasına kayardı — ölçülen 347px'lik kaymanın kabuk payı buydu.
          "fixed inset-y-0 left-0 z-40 w-60 shrink-0 transition-transform",
          "md:static md:z-auto md:translate-x-0 md:transition-[width]",
          expanded ? "md:w-60" : "md:w-16",
          // `invisible`, yalnız `-translate-x-full` değil: ekran dışına itilmiş
          // bir gezinme hâlâ sekmelenebilir olurdu ve odak görünmez bir yere
          // giderdi. Görünürlük onu erişilebilirlik ağacından da çıkarır.
          mobileOpen ? "translate-x-0" : "invisible -translate-x-full md:visible",
        )}
        aria-label={t("nav.primary_label")}
      >
      {/* STAFF banner — INTERNAL identity marker (mockup STAFF banner) */}
      <div
        className={cn(
          "flex items-center gap-2.5 border-b border-[var(--rail-line)] px-3.5 py-2.5",
          !expanded && "justify-center px-2",
        )}
      >
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent-on-inverse)] shadow-[0_0_0_3px_var(--rail-line)]"
        />
        {expanded && (
          <div className="min-w-0 leading-tight">
            <div className="font-sans text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--accent-on-inverse)]">
              {t("app.staff_banner_label")}
            </div>
            <div className="truncate font-serif text-[13px] italic text-[var(--rail-fg-secondary)]">
              {t("app.staff_banner_sub")}
            </div>
          </div>
        )}
      </div>

      {/* Brand wordmark — novem/admin (mockup logo block). The badge is the
          same symbol public/favicon.svg rasterizes, and "same" is asserted on
          all three axes — fill, glyph, and the 26px/8px box the shared
          BRAND_MARK ratio produces — by LeftRail.test.tsx against the RENDERED
          badge. It used to be a 24px/6px box with a --color-neutral-50 letter:
          a mark that differed from its own tab icon in geometry AND in
          foreground while the test stayed green on the fill alone (SEM-6367).

          data-testid because the badge is aria-hidden decoration: it has no
          accessible name, so there is no role/name query that reaches it and
          nothing else in the rail distinguishes it from the footer avatar. */}
      <div className={cn("flex items-center gap-2 px-4 pb-3 pt-4", !expanded && "justify-center px-2")}>
        <div
          aria-hidden="true"
          data-testid="brand-badge"
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[8px] bg-[var(--brand-operator)] font-sans text-[13px] font-bold text-[var(--brand-operator-fg)]"
        >
          {t("app.wordmark_base").slice(0, 1)}
        </div>
        {expanded && (
          <span className="whitespace-nowrap font-sans text-[17px] font-semibold tracking-[-0.02em] text-[var(--fg-on-inverse)]">
            {t("app.wordmark_base")}
            <span className="text-[var(--rail-fg-muted)]">{t("app.wordmark_accent")}</span>
          </span>
        )}
      </div>

      <nav className="flex flex-1 flex-col gap-px overflow-y-auto px-2 pb-2">
        {NAV_SECTIONS.map((section) => {
          // Bölümün TÜM girdileri role kapalıysa başlığı da basma. Denetçi
          // personasında "Ticaret" ve "Ürün" altı bomboş iki etiket olarak
          // kalıyordu (gelir operatöründe "Ürün"); ekran okuyucu da olmayan
          // bir bölümü duyuruyordu. maySee roller çözülene kadar fail-closed
          // olduğundan bu ilk kareyi de sadeleştirir.
          const visibleItems = section.items.filter(maySee);
          if (visibleItems.length === 0) return null;
          return (
          <div key={section.key} className="flex flex-col gap-px">
            {expanded && (
              <p className="px-2.5 pb-1.5 pt-3.5 font-sans text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--rail-fg-muted)]">
                {t(`nav.section_${section.key}`)}
              </p>
            )}
            {visibleItems.map(({ key, icon: Icon, href }) => (
              <NavLink
                key={key}
                to={href}
                end={href === "/"}
                // Collapsed rail hides the text label (no collapse
                // toggle/label is rendered), leaving the link icon-only —
                // aria-label keeps the same i18n name announced in both
                // states. (SEM-351 a11y)
                aria-label={t(`nav.${key}`)}
                // Dar ekranda panel içeriğin ÜSTÜNDE duruyor, o yüzden
                // kapanma tıklamaya bağlı, gidilen yerin değişmesine değil:
                // açık olan sayfanın girdisine dokunmak da paneli kapatmalı.
                onClick={(): void => {
                  setMobileNavOpen(false);
                }}
                className={({ isActive }): string =>
                  cn(
                    "relative flex items-center gap-2.5 rounded-[6px] px-2.5 py-2 font-sans text-sm font-medium no-underline",
                    "text-[var(--rail-fg-secondary)] hover:bg-[var(--rail-line)] hover:text-[var(--fg-on-inverse)]",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
                    isActive &&
                      "bg-[var(--rail-line)] text-[var(--fg-on-inverse)] [&_svg]:text-[var(--accent-on-inverse)]",
                    !expanded && "justify-center",
                  )
                }
              >
                {({ isActive }): React.ReactElement => (
                  <>
                    {isActive && expanded && (
                      <span
                        aria-hidden="true"
                        className="absolute bottom-2 left-0 top-2 w-0.5 rounded-full bg-[var(--accent-on-inverse)]"
                      />
                    )}
                    <Icon
                      size={16}
                      aria-hidden="true"
                      className="shrink-0 text-[var(--rail-fg-muted)]"
                    />
                    {expanded && <span className="truncate">{t(`nav.${key}`)}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </div>
          );
        })}
      </nav>

      {/* Operator footer — avatar + name + sign-out (mockup Sidebar footer) */}
      {auth.isAuthenticated && (
        <div
          className={cn(
            "flex items-center gap-2.5 border-t border-[var(--rail-line)] px-3 py-3",
            !expanded && "justify-center px-2",
          )}
        >
          <div
            aria-hidden="true"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-600)] font-sans text-[11px] font-semibold text-[var(--color-neutral-50)]"
          >
            {initials}
          </div>
          {expanded && (
            <>
              <div className="min-w-0 flex-1">
                <div className="truncate font-sans text-[13px] font-medium text-[var(--fg-on-inverse)]">
                  {userLabel}
                </div>
                <div className="truncate font-serif text-[11px] italic text-[var(--rail-fg-muted)]">
                  {t("app.tagline")}
                </div>
              </div>
              <button
                type="button"
                onClick={(): void => void auth.signoutRedirect()}
                aria-label={t("auth.sign_out")}
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-[var(--rail-fg-muted)]",
                  "hover:bg-[var(--rail-line)] hover:text-[var(--fg-on-inverse)]",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
                )}
              >
                <LogOut size={14} aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      )}
      </aside>
    </>
  );
}
